import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { useUserStore } from '@/store/userStore';
import { isPaidPlan } from '@/utils/planHelpers';
import { Zap, X, ArrowUpRight } from 'lucide-react';

function LowCreditsBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [dismissed, setDismissed] = useState(false);

  const credits = user?.remainingCredit ?? 0;
  const isPro = isPaidPlan(user?.userPlan);

  if (!isPro || credits > 10 || dismissed) return null;

  return (
    <div className="mx-4 md:mx-6 mb-4 p-3.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 flex items-center gap-3 animate-fade-in">
      <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <Zap className="w-5 h-5 text-amber-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">{t('account.lowCredits.title')}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate('/subscription')}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium transition-colors"
        >
          <ArrowUpRight className="w-3.5 h-3.5" />
          <span>{t('subscription.buyCredits')}</span>
        </button>
        <button onClick={() => setDismissed(true)} className="p-1 rounded-md hover:bg-surface-light transition-colors">
          <X className="w-4 h-4 text-text-tertiary" />
        </button>
      </div>
    </div>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const credits = user?.remainingCredit ?? 0;
  const isPro = isPaidPlan(user?.userPlan);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      
      {/* Main Content */}
      <div className="md:ml-64">
        {/* Desktop Header - hidden on mobile */}
        <div className="hidden md:block">
          <Header />
        </div>
        
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-glass-border px-4 py-3 flex items-center justify-between">
          {/* Left - Logo and Name */}
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Vivideo" className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-bold text-text-primary">{t('appName')}</span>
          </div>
          
          {/* Right - Credits */}
          <button
            onClick={() => navigate('/subscription')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-surface border border-primary/30 hover:border-primary/50 transition-all"
          >
            <span className="text-xs text-text-secondary">{t('account.credits')}</span>
            <div className="px-2 py-0.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-[0_0_10px_rgba(139,92,246,0.4)]">
              <span className="text-xs font-bold text-white">{isPro ? credits : t('subscription.free')}</span>
            </div>
          </button>
        </header>
        
        {/* Low credits banner */}
        <LowCreditsBanner />

        {/* Page Content */}
        <main className="p-4 md:p-6 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>
      
      {/* Mobile Bottom Navigation - hidden on desktop */}
      <div className="md:hidden">
        <BottomNav />
      </div>

    </div>
  );
}

